import { moment, i18n } from '@international/mastodon-i18n';
import { startCase, camelCase } from 'lodash';
import { DateObject, DocumentType, FieldConfiguration } from './interfaces';
import { multiDocuments } from './constants';

type DocumentMap = Record<string, Record<string, any[]>>;
type GetDocumentsFn = (field: string) => any;
type SetDocumentsFn = (updater: (prev: DateObject[]) => DateObject[]) => void;
type UpdateDocumentsFn = (exceptionId: string, documents: any) => void;

const CANDIDATE_MULTI_DOCUMENTS_DISCLOSURE_VERSION = '2025-05-08';

// Formats a date string to 'MM/YYYY' or returns 'now' if the date is not provided
const formatDate = (date: string) =>
  date ? moment.utc(date).format('MM/YYYY') : i18n.getStr('labels.now');

// Formats start and end dates
const formatDates = (startDate: string, endDate: string) => {
  return {
    startDateFormatted: formatDate(startDate),
    endDateFormatted: formatDate(endDate),
  };
};

export const getTranslation = (type: string) => {
  const i18nKey = `components.Verification.EmploymentMultidocPanel.${camelCase(
    type,
  )}`;
  const translated = i18n.getStr(i18nKey);
  return translated !== i18nKey ? translated : startCase(type);
};

const formatDocumentTypes = ({
  type,
  requiredForVerification,
}: DocumentType) => {
  const name = getTranslation(type);
  return { value: type, name, disabled: false, requiredForVerification };
};

export const getDocumentOptions = (documentTypes: DocumentType[]) => [
  { enabled: true },
  ...documentTypes.map(formatDocumentTypes),
];

// Get year from formatted date
const getDateYear = (date: string) =>
  date === i18n.getStr('labels.now')
    ? moment.utc().format('YYYY')
    : moment.utc(date, 'MM/YYYY').format('YYYY');

// Maps field configurations to document requirements
const mapFieldConfigurationsToDocuments = (
  fieldConfigurations: FieldConfiguration[],
) => {
  return fieldConfigurations.map(
    ({ id, requestedDocumentAttributes, type }) => ({
      id,
      year: Array.isArray(requestedDocumentAttributes?.year)
        ? requestedDocumentAttributes.year[0]
        : requestedDocumentAttributes?.year,
      exceptionType: type,
    }),
  );
};

// Returns the instruction key according to the package policy
const readPolicy = (
  policy: string,
  index: number,
  isYearly: boolean,
  totalDocs: number,
) => {
  switch (policy) {
    case 'thorough':
      return 'annualDocument';
    case 'complete':
      if (totalDocs === 1) return 'annualDocument';
      return !index ? 'startDateDocument' : 'endDateDocument';
    case 'basic':
    default:
      return isYearly ? 'annualDocument' : 'generalDocument';
  }
};

// Generates initial documents based on field configurations
const generateInitialDocuments = (
  fieldConfigurations: FieldConfiguration[],
) => {
  return mapFieldConfigurationsToDocuments(fieldConfigurations).map(
    ({ id, year, exceptionType }) => ({
      id,
      year,
      value: year?.toString(),
      name: year?.toString(),
      disabled: false,
      uploadId: '',
      exceptionType,
    }),
  );
};

// Checks if a value is a four-digit year
const isFourDigitYear = (value: string) => {
  return /^\d{4}$/.test(value);
};

// Updates a state array at a specific index with a new value
const updateStateArray = (
  setState: any,
  key: string,
  index: number,
  value: any,
) => {
  setState((prevState: any) => {
    const updatedArray = [...prevState[key]];
    updatedArray[index] = value;
    return { ...prevState, [key]: updatedArray };
  });
};

// Updates enabled documents based on uploaded IDs
const updateEnabledDocuments = (
  documents: DateObject[],
  uploadedIds: string[],
) => {
  return documents.map(doc =>
    doc.disabled && doc.uploadId && !uploadedIds.includes(doc.uploadId)
      ? { ...doc, disabled: false, uploadId: '' }
      : doc,
  );
};

// Extracts document requirements from verification data
const extractDocumentRequirements = (verification: any) => {
  const { companyLogoUri = '', fieldConfigurations = [] } = verification;
  const fieldConfiguration = fieldConfigurations[0] || {};
  const requestedAttributes =
    fieldConfiguration.requestedDocumentAttributes || {};
  const {
    startDate = '',
    endDate = '',
    position = '',
    hiringPolicy = '',
    company = '',
    type = '',
  } = requestedAttributes;
  const { startDateFormatted, endDateFormatted } = formatDates(
    startDate,
    endDate,
  );
  const documentsRequired = generateInitialDocuments(fieldConfigurations);

  return {
    company,
    companyLogoUri,
    documentsRequired,
    endDateFormatted,
    hiringPolicy,
    position,
    startDateFormatted,
    type,
  };
};

// Retrieves documents by exception ID, excluding a specific upload ID
const getDocumentsByExceptionId = (
  getDocuments: any,
  exceptionId: string,
  uploadId: string,
) => {
  const exceptionEntries = getDocuments(exceptionId);
  const docType = Object.keys(exceptionEntries ?? {})[0];
  const docsWithUploadId = exceptionEntries?.[docType]?.filter(
    (doc: any) => doc.uploadId !== uploadId,
  );
  return docsWithUploadId?.length ? { [docType]: docsWithUploadId } : undefined;
};

// Splits documents into requested and additional based on conditions
const splitDocuments = (documents: any[], documentsRequired: any[]) => {
  const requestedUploadIdMap = new Map(
    documentsRequired.map(
      ({ type, uploadId, id, exceptionId, requiredForVerification }) => [
        uploadId,
        { type, id: id || exceptionId, requiredForVerification },
      ],
    ),
  );

  return documents.reduce<[any[], any[]]>(
    ([requested, additional], doc) => {
      const exceptionId = requestedUploadIdMap.get(doc.uploadId);
      exceptionId?.requiredForVerification
        ? requested.push({
            ...doc,
            exceptionId: exceptionId.id,
            requiredForVerification: exceptionId?.requiredForVerification,
          })
        : additional.push({
            ...doc,
            exceptionId: exceptionId?.id,
            requiredForVerification: exceptionId?.requiredForVerification,
          });
      return [requested, additional];
    },
    [[], []],
  );
};

// Groups documents into provided, remaining, required, and additional categories
const groupedDocuments = (documents: DateObject[]) => {
  return documents.reduce<{
    documentsProvided: DateObject[];
    remainingDocuments: DateObject[];
    documentsRequired: DateObject[];
    optionalDocuments: DateObject[];
  }>(
    (acc, file) => {
      if (isFourDigitYear(file.value)) {
        acc.documentsRequired.push(file);
        if (file.uploadId !== '') {
          acc.documentsProvided.push(file);
        }
        if (!file.disabled) {
          acc.remainingDocuments.push(file);
        }
      }
      if (file.exceptionType === multiDocuments) {
        acc.optionalDocuments.push(file);
      }
      return acc;
    },
    {
      documentsProvided: [],
      remainingDocuments: [],
      documentsRequired: [],
      optionalDocuments: [],
    },
  );
};

// Updates documents when multiple files are uploaded
const uploadedFilesUpdate = (
  uploadedFiles: any[],
  stateYears: DateObject[],
  updateDocuments: (docId: string, documents: Record<string, any[]>) => void,
  getDocuments: (docId: string) => Record<string, any[]>,
) => {
  const multiDocs = uploadedFiles.reduce<DocumentMap>((acc, file) => {
    const matchingDocument = stateYears.find(document => {
      return document.uploadId === file.uploadId && document?.id;
    });
    const exceptionType = matchingDocument?.exceptionType ?? '';
    if (matchingDocument?.id) {
      file.metadata = {
        context_attributes: {
          year: matchingDocument?.year,
          candidate_disclosure_version:
            CANDIDATE_MULTI_DOCUMENTS_DISCLOSURE_VERSION,
        },
        document_class: matchingDocument.type,
      };
      acc[matchingDocument.id] = {
        [exceptionType]: [
          ...(acc[matchingDocument.id]?.[exceptionType] || []),
          file,
        ],
      };
    }
    return acc;
  }, {});

  Object.entries(multiDocs).forEach(([docId, doc]) => {
    const [docType] = Object.keys(doc);
    updateDocuments(docId, {
      [docType]: [...(getDocuments(docId)?.[docType] || []), ...doc[docType]],
    });
  });
};

// Removes partially uploaded documents when the process is canceled
const onCancel = (setShowModal: any, setDocuments: any, uploadedFiles: any) => {
  setShowModal(false);

  const indexesToRemove = uploadedFiles.map((file: any) => file.uploadId);
  setDocuments((prevDocuments: any) =>
    prevDocuments.filter((doc: any) => !indexesToRemove.includes(doc.uploadId)),
  );
};

// Handles form submission
const onSubmit = (setShowModal: any) => {
  setShowModal(false);
};

const createOnRemoveByUploadId = (
  setDocuments: SetDocumentsFn,
  getDocuments: GetDocumentsFn,
  updateDocuments: UpdateDocumentsFn,
) => {
  return (uploadId: string, exceptionId: string) => {
    setDocuments(prevDocuments =>
      prevDocuments.filter(file => file.uploadId !== uploadId),
    );

    const exceptionDocs = getDocumentsByExceptionId(
      getDocuments,
      exceptionId,
      uploadId,
    );

    updateDocuments(exceptionId, exceptionDocs);
  };
};

export {
  getDateYear,
  extractDocumentRequirements,
  updateEnabledDocuments,
  isFourDigitYear,
  getDocumentsByExceptionId,
  splitDocuments,
  readPolicy,
  updateStateArray,
  uploadedFilesUpdate,
  groupedDocuments,
  onCancel,
  onSubmit,
  createOnRemoveByUploadId,
};
